import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Cookie, Shield, Settings } from 'lucide-react';

const CookieBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const applyConsent = useCallback((consentType) => {
    if (typeof window.gtag === 'function') {
      if (consentType === 'accepted' || consentType === 'all') {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted'
        });
      } else if (consentType === 'analytics') {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      } else {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }
    }
  }, []);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      applyConsent(cookieConsent);
    }
  }, [applyConsent]);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent('all');
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent('necessary');
    setIsVisible(false);
  };

  const handleAcceptAnalytics = () => {
    localStorage.setItem('cookieConsent', 'analytics');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent('analytics');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const content = {
    de: {
      title: 'Cookie-Einstellungen',
      description: 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten und unsere Dienste zu verbessern.',
      acceptAll: 'Alle akzeptieren',
      acceptNecessary: 'Nur notwendige',
      customize: 'Anpassen',
      privacy: 'Datenschutz',
      necessary: 'Notwendige Cookies',
      necessaryDesc: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.',
      analytics: 'Analyse-Cookies',
      analyticsDesc: 'Helfen uns zu verstehen, wie Besucher die Website nutzen.',
      marketing: 'Marketing-Cookies',
      marketingDesc: 'Werden verwendet, um Werbung relevanter zu gestalten.',
      acceptSelected: 'Auswahl akzeptieren',
    },
    en: {
      title: 'Cookie Settings',
      description: 'We use cookies to provide you with the best possible experience on our website and to improve our services.',
      acceptAll: 'Accept All',
      acceptNecessary: 'Necessary Only',
      customize: 'Customize',
      privacy: 'Privacy Policy',
      necessary: 'Necessary Cookies',
      necessaryDesc: 'These cookies are required for basic website functions.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'Help us understand how visitors use the website.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'Used to make advertising more relevant.',
      acceptSelected: 'Accept Selected',
    },
  };

  const t = content[language] || content.de;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] animate-fadeIn" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-slideUp">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
          {/* Header */}
          <div className="bg-neutral-50 px-5 py-4 border-b border-neutral-100 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-xl">
              <Cookie className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="font-bold text-neutral-900 text-lg">{t.title}</h3>
          </div>

          {/* Content */}
          <div className="p-5">
            {!showDetails ? (
              <>
                <p className="text-neutral-600 text-sm leading-relaxed mb-5">
                  {t.description}
                </p>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl shadow-lg shadow-red-600/25 transition-all"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {t.acceptAll}
                  </Button>
                  <Button
                    onClick={handleAcceptNecessary}
                    variant="outline"
                    className="flex-1 border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-100 font-semibold py-3 rounded-xl transition-all"
                  >
                    {t.acceptNecessary}
                  </Button>
                  <Button
                    onClick={() => setShowDetails(true)}
                    variant="ghost"
                    className="flex-1 text-neutral-600 hover:bg-neutral-100 font-semibold py-3 rounded-xl transition-all"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t.customize}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Detailed Settings */}
                <div className="space-y-4 mb-5">
                  {/* Necessary */}
                  <div className="flex items-start justify-between p-4 bg-neutral-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900">{t.necessary}</h4>
                      <p className="text-sm text-neutral-500 mt-1">{t.necessaryDesc}</p>
                    </div>
                    <div className="ml-4">
                      <div className="w-12 h-6 bg-red-600 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between p-4 bg-neutral-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900">{t.analytics}</h4>
                      <p className="text-sm text-neutral-500 mt-1">{t.analyticsDesc}</p>
                    </div>
                    <div className="ml-4">
                      <input 
                        type="checkbox" 
                        id="analytics-check"
                        defaultChecked 
                        className="sr-only peer"
                      />
                      <label 
                        htmlFor="analytics-check"
                        className="w-12 h-6 bg-neutral-300 peer-checked:bg-red-600 rounded-full flex items-center px-1 cursor-pointer transition-colors peer-checked:justify-end"
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </label>
                    </div>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start justify-between p-4 bg-neutral-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900">{t.marketing}</h4>
                      <p className="text-sm text-neutral-500 mt-1">{t.marketingDesc}</p>
                    </div>
                    <div className="ml-4">
                      <input 
                        type="checkbox" 
                        id="marketing-check"
                        defaultChecked 
                        className="sr-only peer"
                      />
                      <label 
                        htmlFor="marketing-check"
                        className="w-12 h-6 bg-neutral-300 peer-checked:bg-red-600 rounded-full flex items-center px-1 cursor-pointer transition-colors peer-checked:justify-end"
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl shadow-lg shadow-red-600/25 transition-all"
                  >
                    {t.acceptAll}
                  </Button>
                  <Button
                    onClick={handleAcceptAnalytics}
                    variant="outline"
                    className="flex-1 border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-100 font-semibold py-3 rounded-xl transition-all"
                  >
                    {t.acceptSelected}
                  </Button>
                </div>
              </>
            )}

            {/* Privacy Link */}
            <div className="mt-4 text-center">
              <a
                href="#/datenschutz"
                className="text-sm text-red-600 hover:text-red-700 font-medium underline-offset-2 hover:underline"
              >
                {t.privacy}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
